import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, CreditCard, Crown, DollarSign, Gift, Sparkles, Type, Users,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell, PageLoader } from "../components/AppShell";
import { api } from "../lib/api";

export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== "admin") {
      nav("/dashboard");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (err) {
        logger.error("Admin stats failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, nav]);

  if (loading) return <PageLoader />;
  if (!stats) {
    return (
      <AppShell>
        <div className="text-center py-20 text-zinc-500">Admin access required.</div>
      </AppShell>
    );
  }

  const cards = [
    { l: "Revenue", v: `$${(stats.total_revenue || 0).toFixed(2)}`, i: DollarSign, c: "text-green-600" },
    { l: "Transactions", v: stats.total_paid_transactions || 0, i: CreditCard, c: "text-blue-600" },
    { l: "Users", v: stats.total_users || 0, i: Users, c: "text-purple-600" },
    { l: "Generations", v: stats.total_generations || 0, i: Sparkles, c: "text-brand" },
    { l: "Words", v: (stats.total_words || 0).toLocaleString(), i: Type, c: "text-orange-600" },
    { l: "Referrals", v: stats.total_referrals || 0, i: Gift, c: "text-pink-600" },
  ];

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid="admin-page">
        <div className="flex items-center gap-3 mb-8">
          <Crown className="w-7 h-7 text-brand" />
          <h1 className="text-3xl font-black tracking-tighter text-zinc-950 font-heading">Admin Dashboard</h1>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {cards.map(({ l, v, i: Icon, c }, idx) => (
            <div key={l} className="bg-white border border-zinc-200 p-5 animate-fade-in" style={{ animationDelay: `${idx * 0.05}s` }} data-testid={`admin-stat-${l.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold tracking-[0.15em] text-zinc-400 uppercase">{l}</p>
                <Icon className={`w-4 h-4 ${c}`} />
              </div>
              <div className="text-2xl font-black tracking-tighter text-zinc-950 font-heading">{v}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 p-6" data-testid="admin-template-stats">
            <h3 className="text-lg font-bold text-zinc-900 mb-4 font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-brand" /> Template Popularity
            </h3>
            {stats.template_stats?.length > 0 ? (
              <div className="space-y-3">
                {stats.template_stats.slice().sort((a, b) => b.count - a.count).map((t) => {
                  const mx = Math.max(...stats.template_stats.map((s) => s.count));
                  return (
                    <div key={t.name}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-zinc-700">{t.name}</span>
                        <span className="text-zinc-400 font-mono text-xs">{t.count}</span>
                      </div>
                      <div className="h-2 bg-zinc-100 overflow-hidden">
                        <div className="h-full bg-brand transition-all duration-700" style={{ width: `${(t.count / mx) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400">No data yet.</p>
            )}
          </div>
          <div className="bg-white border border-zinc-200 p-6" data-testid="admin-recent-payments">
            <h3 className="text-lg font-bold text-zinc-900 mb-4 font-heading flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" /> Recent Payments
            </h3>
            {stats.recent_payments?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_payments.map((p) => (
                  <div key={p.id || p.session_id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{p.package_id} pack</p>
                      <p className="text-xs text-zinc-500">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-bold ${p.payment_status === "paid" ? "text-green-600" : "text-zinc-400"}`}>${(p.amount || 0).toFixed(2)}</span>
                      <p className="text-xs text-zinc-400">{p.payment_status}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400">No payments yet.</p>
            )}
          </div>
          <div className="bg-white border border-zinc-200 p-6 lg:col-span-2" data-testid="admin-recent-users">
            <h3 className="text-lg font-bold text-zinc-900 mb-4 font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" /> Recent Users
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="py-2 pr-4 text-xs font-bold tracking-wider text-zinc-400 uppercase">Name</th>
                    <th className="py-2 pr-4 text-xs font-bold tracking-wider text-zinc-400 uppercase">Email</th>
                    <th className="py-2 pr-4 text-xs font-bold tracking-wider text-zinc-400 uppercase">Credits</th>
                    <th className="py-2 pr-4 text-xs font-bold tracking-wider text-zinc-400 uppercase">Role</th>
                    <th className="py-2 text-xs font-bold tracking-wider text-zinc-400 uppercase">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_users?.map((u) => (
                    <tr key={u.email} className="border-b border-zinc-50 hover:bg-zinc-50">
                      <td className="py-2.5 pr-4 font-medium text-zinc-900">{u.name}</td>
                      <td className="py-2.5 pr-4 text-zinc-600">{u.email}</td>
                      <td className="py-2.5 pr-4 font-mono">{u.credits}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`text-xs font-bold px-2 py-0.5 ${u.role === "admin" ? "bg-brand/10 text-brand" : "bg-zinc-100 text-zinc-600"}`}>{u.role}</span>
                      </td>
                      <td className="py-2.5 text-zinc-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
